import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Text } from "@pentabd/ui";

import {
  InputDateControllerRow,
  InputTextRow,
  MultiFileRow,
  SelectControllerRow,
} from "@components";

import { useGeoData } from "@hooks/useGeoData";
import { useLanguage } from "@hooks/useLanguage";

import { getConvertedGeoData } from "@helpers/geoDataConvert";

import { duration } from "@utils/duration";
import {
  ABROAD_POSTING,
  AbroadPostingsFormDataType,
} from "@validations/HRM/employee/postingsValidation";

import { GEO_DATA } from "@constants/geo-data";

export const AbroadPostingForm = ({
  isProfileDisabled,
}: {
  isProfileDisabled: boolean;
}) => {
  const { t } = useTranslation();
  const { language } = useLanguage();

  const { data: countries } = useGeoData(GEO_DATA.COUNTRIES);

  const {
    register,
    control,
    watch,
    formState: { errors },
    setValue,
  } = useFormContext<AbroadPostingsFormDataType>();

  //This dates will calculate duration
  let fromDate = watch(ABROAD_POSTING.FROM_DATE) as string;
  let toDate = watch(ABROAD_POSTING.TO_DATE) as string;

  return (
    <div className="d-flex flex-column gap-8">
      <SelectControllerRow
        label="EMPLOYEE.POSTINGS_INFO.FORM.POSTING_ABROAD_INFO.COUNTRY"
        name={ABROAD_POSTING.COUNTRY}
        error={errors}
        register={{ ...register(ABROAD_POSTING.COUNTRY) }}
        options={getConvertedGeoData(countries, language)}
        setValue={setValue}
        control={control}
        isSearchable
        required
        disabled={isProfileDisabled}
      />
      <InputTextRow
        register={{ ...register(ABROAD_POSTING.PURPOSE) }}
        label="EMPLOYEE.POSTINGS_INFO.FORM.POSTING_ABROAD_INFO.PURPOSE"
        error={errors}
        required
        disabled={isProfileDisabled}
      />
      <InputDateControllerRow
        label="EMPLOYEE.POSTINGS_INFO.FORM.POSTING_ABROAD_INFO.FROM_DATE"
        control={control}
        register={{ ...register(ABROAD_POSTING.FROM_DATE) }}
        errors={errors}
        name={ABROAD_POSTING.FROM_DATE}
        required
        disabled={isProfileDisabled}
      />
      <InputDateControllerRow
        label="EMPLOYEE.POSTINGS_INFO.FORM.POSTING_ABROAD_INFO.TO_DATE"
        control={control}
        register={{ ...register(ABROAD_POSTING.TO_DATE) }}
        name={ABROAD_POSTING.TO_DATE}
        disabled={isProfileDisabled}
      />

      <div className="row">
        <div className="col-md-12 col-lg-3">
          <Text size="sm" weight="semibold" className="text-title">
            {t(
              "EMPLOYEE.POSTINGS_INFO.FORM.POSTING_ABROAD_INFO.DURATION.LABEL",
            )}
          </Text>
        </div>
        <div className="col-md-12 col-lg-7">
          <Text
            weight="normal"
            size="md"
            component="p"
            color="subtitle2"
            className="px-10 py-5 input-group-wrap input-group-wrap-disabled"
          >
            {duration(fromDate, toDate)
              ? duration(fromDate, toDate)
              : t(
                  "EMPLOYEE.POSTINGS_INFO.FORM.POSTING_ABROAD_INFO.DURATION.PLACEHOLDER",
                )}
          </Text>
          <Text color="warning" size="xs">
            {t(
              "EMPLOYEE.POSTINGS_INFO.FORM.POSTING_ABROAD_INFO.DURATION.RESTRICTION",
            )}
          </Text>
        </div>
      </div>
      <MultiFileRow
        name="document"
        control={control}
        setValue={setValue}
        register={register}
        error={errors}
        fileDetails={["documentName", "date", "remarks", "memorialNo"]}
        disabled={isProfileDisabled}
      />
    </div>
  );
};
